/* Manic Fits — product page add-ons: live blocks, fit finder, COD buy now, notify me, delivery estimate, complete the fit. */
import { StandardEvents } from '@shopify/events';

const SIZE_ALIASES = { XXL: '2XL', '2XL': 'XXL', XXXL: '3XL', '3XL': 'XXXL' };
const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function sectionOf(el) {
  return el.closest('.shopify-section') || document;
}

function currentVariantId(scope) {
  const input = scope.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
  return input ? input.value : null;
}

/* ---------- Live blocks: re-render from the theme's variant-change response ---------- */
class ManicLive extends HTMLElement {
  connectedCallback() {
    this.section = sectionOf(this);
    this.onSelect = (event) => {
      if (!event.promise || (event.target instanceof Element && event.target.closest('product-card'))) return;
      event.promise
        .then(({ detail }) => {
          const html = detail && detail.html;
          if (!html) return;
          const fresh = html.querySelector(`manic-live[data-key="${this.dataset.key}"]`);
          if (fresh) {
            this.innerHTML = fresh.innerHTML;
            Object.assign(this.dataset, fresh.dataset);
            this.sync();
          }
        })
        .catch(() => {});
    };
    this.section.addEventListener(StandardEvents.productSelect, this.onSelect);
    this.sync();
  }
  disconnectedCallback() {
    this.section && this.section.removeEventListener(StandardEvents.productSelect, this.onSelect);
  }
  sync() {
    if (this.dataset.role !== 'notify') return;
    const soldOut = this.dataset.available === 'false';
    const target = this.section === document ? document.documentElement : this.section;
    target.classList.toggle('manic-soldout', soldOut);
  }
}
if (!customElements.get('manic-live')) customElements.define('manic-live', ManicLive);

/* ---------- Fit finder ---------- */
function parseHeightInches(text) {
  const out = [];
  const re = /(\d)\s*'\s*(\d{1,2})?/g;
  let m;
  while ((m = re.exec(text))) out.push(Number(m[1]) * 12 + Number(m[2] || 0));
  return out;
}
function parseKg(text) {
  const nums = (text.match(/\d+(?:\.\d+)?/g) || []).map(Number);
  if (/upto|up to/i.test(text)) return [0, nums[0]];
  if (/\+/.test(text)) return [nums[0], 999];
  return nums.length > 1 ? [nums[0], nums[1]] : [nums[0], nums[0]];
}

class ManicFitFinder extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('script[type="application/json"]');
    if (!data) return;
    try {
      this.chart = JSON.parse(data.textContent);
    } catch (e) {
      return;
    }
    const cols = this.chart.columns.map((c) => c.toLowerCase());
    this.buildCol = cols.findIndex((c) => c.includes('build'));
    this.heightCol = cols.findIndex((c) => c.includes('height'));
    this.weightCol = cols.findIndex((c) => c.includes('weight'));
    this.mode = this.buildCol > -1 ? 'build' : this.heightCol > -1 && this.weightCol > -1 ? 'body' : null;
    if (!this.mode) return;
    this.hidden = false;
    this.querySelector(`[data-mode="${this.mode}"]`).hidden = false;
    this.result = this.querySelector('[data-result]');
    this.addEventListener('click', (e) => {
      const b = e.target.closest('[data-build]');
      if (b) this.pickBuild(Number(b.dataset.build));
      if (e.target.closest('[data-use-size]')) this.useSize();
    });
    this.addEventListener('input', () => this.mode === 'body' && this.fromBody());
    if (this.mode === 'build') {
      const wrap = this.querySelector('[data-builds]');
      this.chart.rows.forEach((row, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'manic-ff__chip';
        btn.dataset.build = i;
        btn.setAttribute('aria-pressed', 'false');
        btn.textContent = row.values[this.buildCol].replace(/\s*build$/i, '');
        wrap.appendChild(btn);
      });
    }
  }
  pickBuild(i) {
    this.querySelectorAll('[data-build]').forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.build) === i)));
    this.show(this.chart.rows[i]);
  }
  fromBody() {
    const ft = Number(this.querySelector('[name="ff-ft"]').value);
    const inch = Number(this.querySelector('[name="ff-in"]').value || 0);
    const kg = Number(this.querySelector('[name="ff-kg"]').value);
    if (!ft || !kg) return this.show(null);
    const h = ft * 12 + inch;
    let best = null;
    let bestScore = Infinity;
    this.chart.rows.forEach((row) => {
      const hr = parseHeightInches(row.values[this.heightCol]);
      const wr = parseKg(row.values[this.weightCol]);
      const hMid = hr.length > 1 ? (hr[0] + hr[1]) / 2 : hr[0] || h;
      const wMid = wr[1] >= 999 ? wr[0] + 5 : wr[0] === 0 ? wr[1] - 5 : (wr[0] + wr[1]) / 2;
      // weight drives fit more than height
      const score = Math.abs(kg - wMid) * 1 + Math.abs(h - hMid) * 1.5;
      if (score < bestScore) {
        bestScore = score;
        best = row;
      }
    });
    this.show(best);
  }
  show(row) {
    this.rec = row ? row.size : null;
    if (!row) {
      this.result.hidden = true;
      return;
    }
    this.result.hidden = false;
    this.result.querySelector('[data-size]').textContent = row.size;
    this.querySelectorAll('[data-size-label]').forEach((el) => (el.textContent = row.size));
    document.dispatchEvent(new CustomEvent('manic:recommended-size', { detail: { size: row.size } }));
  }
  useSize() {
    if (!this.rec) return;
    const section = sectionOf(this);
    const inputs = Array.from(section.querySelectorAll('variant-picker input[type="radio"]')).filter((i) =>
      /size/i.test(i.dataset.optionName || '')
    );
    const want = [this.rec.toUpperCase(), SIZE_ALIASES[this.rec.toUpperCase()]].filter(Boolean);
    const input = inputs.find((i) => want.includes(String(i.value).toUpperCase()));
    if (input && !input.checked) input.click();
    const picker = section.querySelector('variant-picker');
    picker && picker.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
}
if (!customElements.get('manic-fit-finder')) customElements.define('manic-fit-finder', ManicFitFinder);

/* Highlight the recommended row in the size guide */
document.addEventListener('manic:recommended-size', (e) => {
  const size = String(e.detail.size).toUpperCase();
  document.querySelectorAll('.manic-sg tbody tr, .manic-sgc tbody tr').forEach((tr) => {
    const s = String(tr.dataset.size || '').toUpperCase();
    tr.classList.toggle('is-recommended', s === size || SIZE_ALIASES[s] === size);
  });
});

/* ---------- Buy now (cash on delivery) ---------- */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-manic-buy-now]');
  if (!btn) return;
  e.preventDefault();
  const id = currentVariantId(sectionOf(btn));
  if (!id) return;
  btn.setAttribute('aria-busy', 'true');
  btn.disabled = true;
  try {
    const res = await fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
    });
    if (!res.ok) throw new Error('add failed');
    window.location.href = root() + 'checkout';
  } catch (err) {
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
    const msg = btn.parentElement.querySelector('[data-buy-now-error]');
    if (msg) msg.hidden = false;
  }
});

/* ---------- Notify me (Klaviyo back in stock) ---------- */
function klaviyoCompanyId(fallback) {
  if (fallback) return fallback;
  for (const s of document.querySelectorAll('script[src*="klaviyo"]')) {
    const m = s.src.match(/company_id=([A-Za-z0-9]+)/) || s.src.match(/onsite\/js\/([A-Za-z0-9]{6})\/klaviyo\.js/);
    if (m) return m[1];
  }
  return null;
}

async function subscribeKlaviyo(companyId, email, variantId, listId) {
  const headers = { 'Content-Type': 'application/json', revision: '2024-06-15' };
  const bis = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      data: {
        type: 'back-in-stock-subscription',
        attributes: { profile: { data: { type: 'profile', attributes: { email } } }, channels: ['EMAIL'] },
        relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } } },
      },
    }),
  });
  if (!bis.ok) throw new Error('klaviyo bis ' + bis.status);
  if (listId) {
    fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: { profile: { data: { type: 'profile', attributes: { email } } } },
          relationships: { list: { data: { type: 'list', id: listId } } },
        },
      }),
    }).catch(() => {});
  }
}

async function subscribeShopify(email, tags) {
  const body = new URLSearchParams({ form_type: 'customer', utf8: '✓', 'contact[email]': email, 'contact[tags]': tags });
  const res = await fetch(root() + 'contact', { method: 'POST', body });
  if (!res.ok) throw new Error('shopify ' + res.status);
}

document.addEventListener('click', (e) => {
  const open = e.target.closest('[data-manic-notify-open]');
  if (!open) return;
  const wrap = open.closest('.manic-notify');
  wrap.querySelector('.manic-notify__form').hidden = false;
  open.hidden = true;
  const input = wrap.querySelector('input[type="email"]');
  input && input.focus();
});

document.addEventListener('submit', async (e) => {
  const form = e.target.closest('.manic-notify__form');
  if (!form) return;
  e.preventDefault();
  const wrap = form.closest('.manic-notify');
  const email = form.querySelector('input[type="email"]').value.trim();
  const variantId = wrap.dataset.variantId;
  const btn = form.querySelector('button[type="submit"]');
  const err = form.querySelector('[data-error]');
  err.hidden = true;
  btn.disabled = true;
  const inner = form.querySelector('[name="inner_circle"]');
  try {
    const companyId = klaviyoCompanyId(wrap.dataset.klaviyoKey);
    if (companyId) {
      await subscribeKlaviyo(companyId, email, variantId, inner && inner.checked ? wrap.dataset.listId : null);
    } else {
      await subscribeShopify(email, `notify-me, restock-${wrap.dataset.handle}, restock-variant-${variantId}`);
    }
    form.hidden = true;
    const done = wrap.querySelector('.manic-notify__done');
    done.querySelector('[data-email]').textContent = email;
    done.hidden = false;
  } catch (error) {
    try {
      await subscribeShopify(email, `notify-me, restock-${wrap.dataset.handle}, restock-variant-${variantId}`);
      form.hidden = true;
      const done = wrap.querySelector('.manic-notify__done');
      done.querySelector('[data-email]').textContent = email;
      done.hidden = false;
    } catch (e2) {
      err.hidden = false;
      btn.disabled = false;
    }
  }
});

/* ---------- Delivery estimate ---------- */
class ManicDelivery extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const b = e.target.closest('[data-zone]');
      if (b) this.set(b.dataset.zone);
    });
    let zone = 'dhaka';
    try {
      zone = localStorage.getItem('manic-zone') || 'dhaka';
    } catch (e) {}
    this.set(zone);
  }
  set(zone) {
    try {
      localStorage.setItem('manic-zone', zone);
    } catch (e) {}
    this.querySelectorAll('[data-zone]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.zone === zone)));
    const min = Number(this.dataset[zone + 'Min']);
    const max = Number(this.dataset[zone + 'Max']);
    const out = this.querySelector('[data-eta]');
    if (!out || !min) return;
    const fmt = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    const add = (n) => {
      const d = new Date();
      let added = 0;
      while (added < n) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 5) added++; // skip Fridays
      }
      return d;
    };
    out.textContent = min === max ? fmt.format(add(min)) : `${fmt.format(add(min))} – ${fmt.format(add(max))}`;
  }
}
if (!customElements.get('manic-delivery')) customElements.define('manic-delivery', ManicDelivery);

/* ---------- Complete the fit ---------- */
class ManicCompleteFit extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', () => this.total());
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-add-selected]')) this.add(e.target.closest('[data-add-selected]'));
    });
    this.total();
  }
  rows() {
    return Array.from(this.querySelectorAll('[data-fit-item]')).filter((r) => r.querySelector('input[type="checkbox"]').checked);
  }
  total() {
    const rows = this.rows();
    let cents = 0;
    rows.forEach((r) => {
      const sel = r.querySelector('select');
      const opt = sel ? sel.options[sel.selectedIndex] : null;
      cents += Number(opt ? opt.dataset.price : r.dataset.price) || 0;
    });
    const count = this.querySelector('[data-count]');
    if (count) count.textContent = rows.length;
    const sum = this.querySelector('[data-sum]');
    if (sum) {
      const tk = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      sum.textContent = 'Tk ' + tk;
    }
    const btn = this.querySelector('[data-add-selected]');
    if (btn) btn.disabled = rows.length === 0;
  }
  async add(btn) {
    const items = this.rows()
      .map((r) => {
        const sel = r.querySelector('select');
        return { id: Number(sel ? sel.value : r.dataset.variantId), quantity: 1 };
      })
      .filter((i) => i.id);
    if (!items.length) return;
    btn.disabled = true;
    try {
      const res = await fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error();
      window.location.href = root() + 'cart';
    } catch (e) {
      btn.disabled = false;
      const err = this.querySelector('[data-error]');
      if (err) err.hidden = false;
    }
  }
}
if (!customElements.get('manic-complete-fit')) customElements.define('manic-complete-fit', ManicCompleteFit);

/* ---------- Eyebrow above the product title (product type, "New" for recent drops) ---------- */
function placeEyebrow() {
  const src = document.querySelector('.manic-ff-wrap[data-eyebrow]');
  if (!src || !src.dataset.eyebrow) return;
  const details = src.closest('.product-details');
  const h1 = details && details.querySelector('h1');
  if (!h1 || details.querySelector('.manic-eyebrow--title')) return;
  const p = document.createElement('p');
  p.className = 'manic-eyebrow manic-eyebrow--title';
  p.textContent = src.dataset.eyebrow;
  h1.parentNode.insertBefore(p, h1);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', placeEyebrow);
else placeEyebrow();
